import {
  PlatzBlobViewTokensItem,
  PlatzWordTokenView,
  WordView,
  WordViewLanguage,
} from "../api-generated";
import { getTokenListString } from "./tokens";
import { isWordToken } from "./discriminatorsAndThings";

type JustTextAndLanguage = { text: string; justLanguageHere: WordViewLanguage };

type ToSearch = JustTextAndLanguage | WordView | PlatzBlobViewTokensItem[];

function isJustTextAndLanguage(
  toSearch: ToSearch
): toSearch is JustTextAndLanguage {
  return (toSearch as JustTextAndLanguage).justLanguageHere !== undefined;
}

function isWord(toSearch: ToSearch): toSearch is WordView {
  return (
    (toSearch as WordView).id !== undefined &&
    (toSearch as WordView).string !== undefined &&
    (toSearch as WordView).displayString !== undefined &&
    (toSearch as WordView).language !== undefined
  );
}

//TODO: maybe this should be somewhere else
function getToSearchPrimaryLanguage(toSearch: ToSearch): WordViewLanguage {
  if (isJustTextAndLanguage(toSearch)) {
    return toSearch.justLanguageHere;
  }

  if (isWord(toSearch)) {
    return toSearch.language!;
  }

  const tokenArray = Array.isArray(toSearch) ? toSearch : [toSearch];
  const wordTokens: PlatzWordTokenView[] = tokenArray.filter(isWordToken);
  const counts: Map<WordViewLanguage, number> = new Map();
  let maxCount = 0;
  let mostRepresentedFoo: WordViewLanguage | undefined;

  for (const wordToken of wordTokens) {
    const mapKey: WordViewLanguage = wordToken.word!.language!;
    const prevCount = counts.get(mapKey);
    const newCount = (prevCount || 0) + 1;
    counts.set(mapKey, newCount);
    if (newCount > maxCount) {
      maxCount = newCount;
      mostRepresentedFoo = wordToken.word!.language!;
    }
  }

  if (mostRepresentedFoo === undefined) {
    throw new Error("No language found in getToSearchPrimaryLanguage");
  }

  return mostRepresentedFoo;
}

//SNIPPET - for typeguards in general, and also for how to check for primitive type
function getToSearchString(toSearch: ToSearch): string {
  if (typeof toSearch === "string") {
    return toSearch;
  }

  if (isJustTextAndLanguage(toSearch)) {
    return toSearch.text;
  }

  if (isWord(toSearch)) {
    return toSearch.string!;
  }

  const tokenArray = Array.isArray(toSearch) ? toSearch : [toSearch];
  return getTokenListString(tokenArray);
}

export function searchGoogleTranslate(toSearch: ToSearch): void {
  const toSearchLanguage = getToSearchPrimaryLanguage(toSearch);
  const toSearchString = getToSearchString(toSearch);
  let url;
  if (toSearchLanguage === "GERMAN") {
    url = `https://translate.google.com/?hl=en&sl=de&tl=en&text=${toSearchString}&op=translate`;
  } else if (toSearchLanguage === "SPANISH") {
    url = `https://www.spanishdict.com/translate/${toSearchString}`;
  } else {
    throw new Error(
      "Trying to search unsupported language: " +
        toSearchLanguage?.toString() || "undefined"
    );
  }
  const newTab = window.open(url, "_blank");
  if (newTab) {
    newTab.focus();
  }
}

//`https://www.linguee.com/english-german/search?source=german&query=${text}`;
//`https://www.wordreference.com/deen/${text}`;

export function searchGoogle(toSearch: ToSearch): void {
  const toSearchString = getToSearchString(toSearch);
  const formattedQuery = encodeURIComponent(toSearchString.trim());
  const googleSearchUrl = `https://www.google.com/search?q=${formattedQuery}`;
  const newTab = window.open(googleSearchUrl, "_blank");
  if (newTab) {
    newTab.focus();
  }
}
